import { DatePicker, Modal, Select, Space, Spin, Table, Tag } from 'antd'
import { BaseButton } from 'components/elements/BaseButton'
import { globalConfirm } from 'components/elements/global'
import {
 useGetRecurringEventsQuery,
 useUpdateRecurringEventMutation,
} from 'features/leads/api'
import {
 TRecurringTaskData,
 TRecurringTaskType,
 TTaskEventActionPost,
} from 'features/leads/types'
import { baseTime } from 'lib'
import { TBaseTime } from 'lib/baseTime'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import useFetchPage2 from 'hooks/useFetchPage2'
import useFetchPage from 'hooks/useFetchPage'
import RecurringTaskEditModal from './RecurringTaskEditModal'

const selectAllOptions = [
 { value: 'all', label: 'All Tasks' },
 { value: 'completed', label: 'Completed Tasks' },
 { value: 'pending', label: 'Pending Tasks' },
 { value: 'overdue', label: 'Overdue Tasks' },
]

const RecurringTaskModal: FC<{
 onSuccess?: () => void
 leadId?: any
 taskName?: any
 visible?: boolean
 recurringEventId: number
 onCancel?: () => void
}> = ({ onCancel, recurringEventId, visible, taskName, leadId, onSuccess }) => {
 const tableRef = useRef<HTMLDivElement>(null)
 const [editData, setEditData] = useState<TRecurringTaskData | null>(null)
 const [showEditModal, setShowEditModal] = useState(false)
 const [selectAll, setSelectAll] = useState(false)
 const [unselected, setUnSelected] = useState<number[]>([])
 const [selectedType, setSelectedType] = useState<TRecurringTaskType>('all')
 const [selectedDate, setSelectedDate] = useState<{
  from_date: TBaseTime
  to_date: TBaseTime
 } | null>(null)
 const [selectedRows, setSelectedRows] = useState<any[]>([])

 const [updateEvent] = useUpdateRecurringEventMutation()

 const dateFilter = useMemo(
  () =>
   selectedDate
    ? {
       from_date: baseTime(selectedDate.from_date).format('DD/MM/YYYY'),
       to_date: baseTime(selectedDate.to_date).format('DD/MM/YYYY'),
      }
    : null,
  [selectedDate],
 )
 const {
  isLoading,
  isFetching,
  isRefetching,
  flatData,
  isFetchingNextPage,
  hasMore,
  fetchMore,
  totalCount,
  refetch,
 } = useFetchPage<TRecurringTaskData>('get-recurring-events', {
  method: 'post',
  body: useMemo(
   () => ({
    recurring_events_id: recurringEventId,
    select_all_type: selectedType,
    ...dateFilter,
   }),
   [dateFilter, recurringEventId, selectedType],
  ),
 })

 //  const { data, isLoading, isFetching, refetch } = useGetRecurringEventsQuery(
 //   {
 //    recurring_events_id: recurringEventId,
 //    select_all_type: selectedType,
 //    ...dateFilter,
 //   },
 //   { refetchOnMountOrArgChange: true },
 //  )

 const firstTask = useMemo(
  () =>
   selectedRows?.length > 0
    ? flatData?.find((v) => v.id === selectedRows[0])
    : null,
  [flatData, selectedRows],
 )

 const buildMutationPayload = (
  action: 'complete' | 'delete',
 ): TTaskEventActionPost => {
  const common: TTaskEventActionPost = {
   lead_id: leadId,
   recurring_events_id: recurringEventId,
   action,
  }
  return selectAll
   ? {
      ...common,
      select_all: true,
      unselected,
      select_all_type: selectedType,
      ...(selectedDate && {
       filter_info: JSON.stringify(dateFilter),
      }),
     }
   : {
      ...common,
      task_id: selectedRows,
     }
 }

 const handleAction = (action: 'complete' | 'delete', message: string) => {
  globalConfirm({
   content: message,
   onOk: async () => {
    await updateEvent(buildMutationPayload(action)).unwrap()
    refetch()
    setSelectAll(false)
    setUnSelected([])
    setSelectedRows([])
    onSuccess?.()
   },
  })
 }

 const handleScroll = (e: Event) => {
  const target = e.target as HTMLElement
  const { scrollTop, scrollHeight, clientHeight } = target
  if (!isLoading && hasMore && scrollTop + clientHeight >= scrollHeight - 10) {
   fetchMore()
  }
 }

 useEffect(() => {
  const container = tableRef.current?.querySelector('.ant-table-body')
  if (container && flatData && flatData?.length > 0) {
   container.addEventListener('scroll', handleScroll)
  }
  return () => {
   container?.removeEventListener('scroll', handleScroll)
  }
 }, [flatData])

 useEffect(() => {
  if (selectAll && flatData && flatData?.length > 0) {
   const newunSelected = new Set(unselected)
   const result = flatData.filter((obj) => !newunSelected.has(obj.id))
   setSelectedRows(result.map((v) => v.id))
  }
 }, [flatData, selectAll, unselected])

 return (
  <Modal
   footer={null}
   width={700}
   title={taskName}
   onCancel={onCancel}
   visible={visible}
  >
   {/* <pre>{JSON.stringify(flatData, null, 2)}</pre> */}
   {(selectAll || selectedRows?.length > 0) && (
    <Space
     className="w-100"
     align="end"
     style={{ marginBottom: 16, justifyContent: 'flex-end' }}
    >
     <Tag className="py-1 px-2" style={{ fontSize: '15px' }} color="red">
      Selected Task :
      {selectAll
       ? Number(totalCount ?? 0) - Number(unselected.length || 0)
       : selectedRows.length ?? 0}{' '}
     </Tag>
     {selectedType === 'overdue' && (
      <BaseButton
       onClick={() =>
        handleAction('complete', 'Are you sure want mark completed')
       }
       icon={<i className="fas fa-check me-1" />}
       type="primary"
      >
       Mark as Completed
      </BaseButton>
     )}
     <BaseButton
      onClick={() =>
       handleAction('delete', 'Are you sure want delete the selected task?')
      }
      icon={<i className="fas fa-trash me-1" />}
      type="primary"
     >
      Delete
     </BaseButton>
     {selectedType === 'pending' && (
      <BaseButton
       onClick={() => setShowEditModal(true)}
       buttonStyle="outline"
       icon={<i className="fas fa-edit me-1" />}
       type="primary"
      >
       Edit
      </BaseButton>
     )}
    </Space>
   )}

   <Space style={{ marginBottom: 16 }}>
    <Select
     value={selectedType}
     onChange={(v) => {
      setSelectedType(v)
      setSelectedRows([])
      setSelectAll(false)
      setUnSelected([])
     }}
     placeholder="Select Status"
     style={{ width: 200 }}
     options={selectAllOptions}
    />
    <DatePicker.RangePicker
     style={{ flex: 1 }}
     onChange={(date) => {
      if (date) {
       setSelectedDate({
        from_date: date[0] as any,
        to_date: date[1] as any,
       })
      }
     }}
     format="YYYY-MM-DD"
     allowClear={false}
     value={
      selectedDate ? [selectedDate.from_date, selectedDate.to_date] : undefined
     }
    />
    {(selectedDate || selectedType !== 'all') && (
     <BaseButton
      onClick={() => {
       setSelectedDate(null)
       setSelectedType('all')
       setSelectedRows([])
       //  onSuccess?.()
      }}
      buttonStyle="solid"
      buttonType="danger"
      icon={<i className="fas fa-undo" />}
     />
    )}
   </Space>
   <div ref={tableRef}>
    <Table
     sticky
     scroll={{ x: 'max-content', y: 400 }}
     pagination={{ hideOnSinglePage: true, pageSize: 1000 }}
     loading={isFetchingNextPage ? false : isLoading || isRefetching}
     rowSelection={{
      selectedRowKeys: selectedRows,
      onSelect: (item, isSelected) => {
       if (selectAll) {
        setUnSelected((prev) =>
         isSelected ? prev.filter((v) => v !== item.id) : [...prev, item.id],
        )
       }
      },
      onChange: (selectedRowKeys, _, { type }) => {
       if (type === 'all') {
        setSelectAll(selectedRowKeys.length > 0)
        setUnSelected([])
       }
       setSelectedRows(selectedRowKeys)
      },
     }}
     rowKey="id"
     dataSource={flatData?.map((v) => ({
      id: v.id,
      name: v.name,
      status: v.status,
      dateTime: v.date
       ? baseTime(`${v.date} ${v.time}`, 'YYYY-MM-DD hh:mm:ss').format(
          'DD MMM YYYY hh:mm A',
         )
       : 'No date/time set',
      task_status: v.task_status,
     }))}
     size="small"
     columns={[
      { dataIndex: 'name', title: 'Name' },
      { dataIndex: 'dateTime', title: 'Date' },
      {
       dataIndex: 'task_status',
       title: 'Status',
       render: (v) => (
        <Tag
         className="text-capitalize"
         color={
          v === 'completed' ? 'green' : v === 'overdue' ? 'red' : 'orange'
         }
        >
         {v}
        </Tag>
       ),
      },
      ...(selectedType === 'overdue' || selectedType === 'completed'
       ? [
          {
           dataIndex: 'action',
           title: '',
           render: (_S: any, rw: any) => (
            <BaseButton
             disabled={selectedRows.length > 1}
             onClick={() => {
              const itemData = flatData?.find((v) => v.id === rw.id)
              setShowEditModal(true)
              if (itemData) {
               setEditData(itemData)
              }
             }}
             size="small"
             icon={<i className="fas fa-edit" />}
            />
           ),
          },
         ]
       : []),
     ]}
    />
    {isFetchingNextPage && (
     <div style={{ textAlign: 'center', padding: 10 }}>
      <Spin />
     </div>
    )}
   </div>

   {showEditModal && (firstTask || editData) && (
    <RecurringTaskEditModal
     editableType={
      editData || selectedRows.length === 1 ? 'single' : 'multiple'
     }
     filterInfo={dateFilter && selectAll ? JSON.stringify(dateFilter) : ''}
     selectedType={selectedType}
     selectedRows={editData ? [editData.id] : selectedRows}
     leadId={leadId}
     selectAll={selectAll}
     unselected={unselected}
     onSuccess={() => {
      setShowEditModal(false)
      setSelectedRows([])
      setSelectAll(false)
      setUnSelected([])
      setEditData(null)
      onSuccess?.()
      refetch()
     }}
     editData={(editData || firstTask) as any}
     visible={showEditModal}
     onCancel={() => {
      setShowEditModal(false)
      setEditData(null)
     }}
    />
   )}
  </Modal>
 )
}

export default RecurringTaskModal
